<template>
  <div v-loading="loading" element-loading-spinner="atom-audit-loader">
    <div v-if="applicableTypes.length === 0" class="no-types-msg">
      No Activity Audit types are configured for this record type.
    </div>
    <div v-else class="hiaTable activity-audit-table-wrap">
      <table ref="activityAuditGrid" id="activityAuditGrid" class="activity-audit-table">
        <thead>
          <tr>
            <th v-if="showCodeType" class="codeTypeCol" title="Required">Code Type</th>
            <th class="activityCascadeCol">Activity type &amp; activity</th>
            <th class="notesCol">Worksheet Notes</th>
            <th class="handledCol">Handled</th>
            <th class="excludeCol">Exclude</th>
            <th class="reasonCol">Reason for Change</th>
            <th class="deleteCol">&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in activityAuditRecords" :key="'aar-' + row.activityAuditRecordId + '-' + row.activityAuditActivityId" class="activity-row">
            <td v-if="showCodeType" class="codeTypeCol">
              <hia-el-select class="audit-cell-control" style="width: 100%;" :disabled="disabled" v-model="row.codeTypeId" placeholder="Code Type" :filterable="true" itemkey="key" label="value" :dropdowns="dropdowns.codeTypes.list" @input="saveRow(row)"> </hia-el-select>
            </td>
            <td class="activityCascadeCol">
              <el-cascader :value="cascaderPathForRow(row)" :options="cascaderOptions" :props="cascaderFieldProps" :disabled="disabled" :clearable="false" filterable expand-trigger="hover" size="mini" placeholder="Select type, then activity" class="activity-audit-cascader" style="width: 100%" @change="onRowCascaderChange(row, $event)"> </el-cascader>
            </td>
            <td class="notesCol">
              <el-input class="activity-audit-notes-input" :disabled="disabled" :value="notesForRow(row)" placeholder="Notes" size="mini" type="textarea" :rows="2" @input="onNotesInput(row, $event)" @change="onNotesChange(row, $event)"> </el-input>
            </td>
            <td class="handledCol">
              <div class="handled-cell-inner">
                <div class="handled-radio-row">
                  <label>
                    <input :disabled="disabled" type="radio" :name="`handled_${row.activityAuditRecordId}`" :value="true" v-model="row.handledCorrectly" @change="handleHandledChange(row)" />
                    Correctly
                  </label>
                  <label>
                    <input :disabled="disabled" type="radio" :name="`handled_${row.activityAuditRecordId}`" :value="false" v-model="row.handledCorrectly" @change="handleHandledChange(row)" />
                    Incorrectly
                  </label>
                </div>
                <i v-if="row.handledCorrectly == null" class="handled-missing-warn mdi mdi-alert" title="Please select Correctly or Incorrectly for Handled." aria-label="Please select Correctly or Incorrectly for Handled." />
              </div>
            </td>
            <td class="excludeCol">
              <iconCheckBox class="audit-exclude-icon" v-model="row.exclude" title="Exclude from reporting denominator" icon="mdi mdi-alpha-e" :disabled="disabled || row.handledCorrectly !== false" @change="saveRow(row)"> </iconCheckBox>
            </td>
            <td class="reasonCol">
              <hia-el-select class="activity-audit-reason-select audit-cell-control" style="width: 100%;" :disabled="disabled || row.handledCorrectly !== false" v-model="row.reasonForChangeId" placeholder="Reason for Change" :filterable="true" clearable itemkey="key" label="value" :dropdowns="reasonDropdownForRow(row)" @input="saveRow(row)"> </hia-el-select>
            </td>
            <td class="deleteCol">
              <i v-if="!disabled" title="Delete Row" class="el-icon-close delete-button activity-audit-delete-x" @click="deleteRow(row)"></i>
            </td>
          </tr>

          <tr class="add-row">
            <td v-if="showCodeType" class="codeTypeCol">
              <hia-el-select class="audit-cell-control" style="width: 100%;" :disabled="disabled" v-model="addCodeTypeId" placeholder="Select code type" :filterable="true" itemkey="key" label="value" :dropdowns="dropdowns.codeTypes.list"> </hia-el-select>
            </td>
            <td class="activityCascadeCol">
              <el-cascader v-model="addCascaderValue" :options="cascaderOptions" :props="cascaderFieldProps" :disabled="disabled" clearable filterable expand-trigger="hover" size="mini" placeholder="Select type, then activity" class="activity-audit-cascader" style="width: 100%"> </el-cascader>
            </td>
            <td class="notesCol">&nbsp;</td>
            <td class="handledCol">&nbsp;</td>
            <td class="excludeCol">&nbsp;</td>
            <td class="reasonCol">
              <el-popover
                ref="addPopover"
                style="word-break: normal;"
                placement="bottom"
                title=""
                width="300"
                trigger="manual"
                popper-class="popover"
                :content="addPopoverContent"
                v-model="showAddPopover"
                @after-enter="handleHidePopOver('addPopover')"
              >
                <el-button
                  :disabled="disabled"
                  round
                  slot="reference"
                  style="float: right;"
                  icon="el-icon-plus"
                  type="primary"
                  size="mini"
                  @click="addRow"
                >
                  Add
                </el-button>
              </el-popover>
            </td>
            <td class="deleteCol">&nbsp;</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
  import { mapState, mapActions, mapMutations } from 'vuex'
  import iconCheckBox from '@/components/controls/iconCheckBox'

  export default {
    name: 'ApplyActivityAudit',
    components: {
      iconCheckBox
    },
    props: {
      recordType: {
        type: String,
        required: true,
        validator: value => ['IP', 'OP', 'AMB', 'PRO'].indexOf(value) !== -1
      },
      recordId: {
        type: Number
      },
      disabled: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        loadingCount: 0,
        addCascaderValue: [],
        addCodeTypeId: null,
        addPopoverContent: '',
        showAddPopover: false
      }
    },
    computed: {
      /** True while initializing or any activity-audit POST/PUT/DELETE is in flight (supports concurrent requests). */
      loading() {
        return this.loadingCount > 0
      },
      ...mapState('activityAuditRecords/', ['activityAuditTypes', 'activityAuditRecords', 'activityAuditReasonsForChange']),
      ...mapState('dropdowns/', ['dropdowns']),
      ...mapState('inpatientReviews/', ['inpatientReview']),
      ...mapState('outpatientReviews/', ['outpatientReview']),
      ...mapState('proReviews/', ['proReview']),
      resolvedRecordId() {
        if (this.recordId) return this.recordId
        switch (this.recordType) {
          case 'IP':
            return this.inpatientReview && this.inpatientReview.drgTableId
          case 'OP':
          case 'AMB':
            return this.outpatientReview && this.outpatientReview.ascTableId
          case 'PRO':
            return this.proReview && this.proReview.proRecordId
        }
        return null
      },
      applicableTypes() {
        return this.activityAuditTypes || []
      },
      showCodeType() {
        return this.recordType !== 'IP'
      },
      defaultCodeTypeId() {
        switch (this.recordType) {
          case 'PRO':
            return this.proReview && this.proReview.project ? this.proReview.project.defaultCodeType : null
          case 'OP':
          case 'AMB':
            return this.outpatientReview && this.outpatientReview.project ? this.outpatientReview.project.defaultCodeType : null
          default:
            return null
        }
      },
      cascaderFieldProps() {
        return {
          value: 'value',
          label: 'label',
          children: 'children',
          emitPath: true
        }
      },
      cascaderOptions() {
        const base = this.buildBaseCascaderOptions()
        const options = JSON.parse(JSON.stringify(base))
        const records = this.activityAuditRecords || []
        for (const r of records) {
          const tid = r.activityAuditTypeId != null ? Number(r.activityAuditTypeId) : null
          const aid = r.activityAuditActivityId != null ? Number(r.activityAuditActivityId) : null
          if (tid == null || aid == null) continue
          let typeNode = options.find(o => o.value === tid)
          if (!typeNode) {
            options.push({
              value: tid,
              label: this.getTypeName(tid) || `Type ${tid}`,
              children: []
            })
            typeNode = options[options.length - 1]
          }
          if (!typeNode.children.some(c => c.value === aid)) {
            typeNode.children.push({
              value: aid,
              label: this.findActivityLabel(aid)
            })
          }
        }
        return options
      },
      addActivityId() {
        return this.addCascaderValue && this.addCascaderValue.length === 2 ? Number(this.addCascaderValue[1]) : null
      },
      /** OP / AMB / PRO only. Null when project default and user selection are both missing. */
      resolvedAddCodeTypeId() {
        if (!this.showCodeType) return null
        return this.addCodeTypeId != null ? this.addCodeTypeId : this.defaultCodeTypeId
      }
    },
    methods: {
      ...mapActions('activityAuditRecords/', ['GET_ACTIVITY_AUDIT_TYPES', 'GET_ACTIVITY_AUDIT_REASONS_FOR_CHANGE', 'POST_ACTIVITY_AUDIT_RECORD', 'PUT_ACTIVITY_AUDIT_RECORD', 'DELETE_ACTIVITY_AUDIT_RECORD']),
      ...mapMutations('activityAuditRecords/', ['UPDATE_ACTIVITY_AUDIT_RECORD', 'SET_ACTIVITY_AUDIT_TYPES']),
      ...mapActions('dropdowns/', ['GET_DROPDOWNS']),
      async withLoading(fn) {
        this.loadingCount++
        try {
          return await fn()
        } finally {
          this.loadingCount--
        }
      },
      async initialize() {
        if (!this.resolvedRecordId) return
        await this.withLoading(async () => {
          try {
            const dropdownKeys = this.showCodeType ? ['codeTypes'] : []
            if (dropdownKeys.length) {
              await this.GET_DROPDOWNS(dropdownKeys)
            }
            await this.GET_ACTIVITY_AUDIT_REASONS_FOR_CHANGE()
          } catch (e) {
            console.log(e)
          }
          this.syncAddRowCodeType()
        })
      },
      reasonDropdownForRow(row) {
        const tid = row.activityAuditTypeId != null ? Number(row.activityAuditTypeId) : null
        const reasons = this.activityAuditReasonsForChange || []
        if (tid == null) return []
        return reasons
          .filter(r => (r.activityAuditTypeIds || []).includes(tid))
          .map(r => ({
            key: r.reasonForChangeId,
            value: r.description || ''
          }))
      },
      async loadActivityAuditTypesForRecord() {
        try {
          await this.GET_ACTIVITY_AUDIT_TYPES({ recordType: this.recordType })
        } catch (e) {
          console.log(e)
        }
      },
      syncAddRowCodeType() {
        if (!this.showCodeType) {
          this.addCodeTypeId = null
          return
        }
        this.addCodeTypeId = this.defaultCodeTypeId != null ? this.defaultCodeTypeId : null
      },
      buildBaseCascaderOptions() {
        return (this.applicableTypes || []).map(t => ({
          value: Number(t.activityAuditTypeId),
          label: t.name,
          children: (t.activities || []).map(a => ({
            value: Number(a.activityAuditActivityId),
            label: a.name
          }))
        }))
      },
      findActivityLabel(activityId) {
        const aid = Number(activityId)
        for (const t of this.activityAuditTypes || []) {
          const a = (t.activities || []).find(x => Number(x.activityAuditActivityId) === aid)
          if (a) return a.name
        }
        return `Activity ${aid}`
      },
      getTypeName(typeId) {
        const tid = typeId != null ? Number(typeId) : null
        const type = (this.activityAuditTypes || []).find(t => Number(t.activityAuditTypeId) === tid)
        return type ? type.name : ''
      },
      cascaderPathForRow(row) {
        const tid = row.activityAuditTypeId != null ? Number(row.activityAuditTypeId) : null
        const aid = row.activityAuditActivityId != null ? Number(row.activityAuditActivityId) : null
        if (tid == null || aid == null) return []
        return [tid, aid]
      },
      async onRowCascaderChange(row, pathVal) {
        if (!pathVal || pathVal.length < 2) return
        const fullRow = this.activityAuditRecords.find(r => r.activityAuditRecordId === row.activityAuditRecordId)
        if (!fullRow) return
        const typeId = Number(pathVal[0])
        const activityId = Number(pathVal[1])
        if (Number(fullRow.activityAuditActivityId) === activityId && Number(fullRow.activityAuditTypeId) === typeId) return
        fullRow.activityAuditActivityId = activityId
        fullRow.activityAuditTypeId = typeId
        const opts = this.reasonDropdownForRow(fullRow)
        if (fullRow.reasonForChangeId != null && !opts.some(o => o.key === fullRow.reasonForChangeId)) {
          fullRow.reasonForChangeId = null
        }
        await this.saveRow(fullRow)
      },
      buildRecordIdFields() {
        const payload = { drgTableId: null, ascTableId: null, proRecordId: null }
        const id = this.resolvedRecordId
        if (!id) return payload
        if (this.recordType === 'IP') payload.drgTableId = id
        else if (this.recordType === 'OP' || this.recordType === 'AMB') payload.ascTableId = id
        else if (this.recordType === 'PRO') payload.proRecordId = id
        return payload
      },
      notesForRow(row) {
        const r = (this.activityAuditRecords || []).find(x => x.activityAuditRecordId === row.activityAuditRecordId)
        if (!r) return ''
        return r.notes != null ? r.notes : ''
      },
      onNotesInput(row, value) {
        const fullRow = (this.activityAuditRecords || []).find(r => r.activityAuditRecordId === row.activityAuditRecordId)
        if (!fullRow) return
        fullRow.notes = value
      },
      async onNotesChange(row, value) {
        const fullRow = (this.activityAuditRecords || []).find(r => r.activityAuditRecordId === row.activityAuditRecordId)
        if (!fullRow) return
        if (value !== undefined) {
          fullRow.notes = value
        }
        await this.saveRow(fullRow)
      },
      handleHidePopOver(ref) {
        const popper = this.$refs[ref]
        if (!popper) return
        const handler = (e) => {
          if (e.target !== popper.$el) {
            popper.doDestroy()
            popper.doClose()
          }
          document.removeEventListener('click', handler, false)
        }
        document.addEventListener('click', handler, false)
      },
      async addRow() {
        if (!this.addActivityId) {
          this.addPopoverContent = 'Activity type and activity must be selected to add row.'
          this.showAddPopover = true
          return
        }
        if (this.showCodeType && this.resolvedAddCodeTypeId == null) {
          this.addPopoverContent = 'Code type must be populated to add row.'
          this.showAddPopover = true
          return
        }
        const pathSnapshot = Array.isArray(this.addCascaderValue) ? [...this.addCascaderValue] : []
        await this.withLoading(async () => {
          try {
            const payload = {
              activityAuditActivityId: this.addActivityId,
              handledCorrectly: null,
              exclude: false,
              notes: null,
              reasonForChangeId: null,
              codeTypeId: this.showCodeType ? this.resolvedAddCodeTypeId : null,
              ...this.buildRecordIdFields()
            }
            const created = await this.POST_ACTIVITY_AUDIT_RECORD({ data: payload, recordType: this.recordType })
            this.addCascaderValue = []
            if (created && pathSnapshot.length === 2) {
              this.UPDATE_ACTIVITY_AUDIT_RECORD({
                ...created,
                activityAuditTypeId: Number(pathSnapshot[0])
              })
            }
            this.$message({ message: 'Activity Audit row added', type: 'success' })
          } catch (e) {
            this.$message({ message: `Error adding row: ${e.message}`, type: 'error' })
          }
        })
      },
      async handleHandledChange(row) {
        // Moved to Correctly while there are dependent values that need to be cleared
        if (row.handledCorrectly === true && (row.reasonForChangeId || row.exclude)) {
          try {
            await this.$confirm('This will clear the Reason for Change and Exclude flag. Continue?', 'Warning', { confirmButtonText: 'OK', cancelButtonText: 'Cancel', type: 'warning' })
            row.reasonForChangeId = null
            row.exclude = false
          } catch (e) {
            // User canceled - revert the radio by forcing a re-render from Vuex state
            this.$forceUpdate()
            return
          }
        } else if (row.handledCorrectly !== false) {
          row.reasonForChangeId = null
          row.exclude = false
        }

        await this.saveRow(row)
      },
      async saveRow(row) {
        if (!row.activityAuditRecordId) return
        await this.withLoading(async () => {
          try {
            const payload = {
              ...row,
              ...this.buildRecordIdFields()
            }
            await this.PUT_ACTIVITY_AUDIT_RECORD({ data: payload, recordType: this.recordType })
          } catch (e) {
            this.$message({ message: `Error saving row: ${e.message}`, type: 'error' })
          }
        })
      },
      async deleteRow(row) {
        await this.$confirm('Delete this Activity Audit row?', 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning'
        })

        await this.withLoading(async () => {
          try {
            await this.DELETE_ACTIVITY_AUDIT_RECORD({ row, recordType: this.recordType })
            this.$message({ message: 'Activity Audit row deleted', type: 'success' })
          } catch (e) {
            this.$message({ message: `Error deleting row: ${e.message}`, type: 'error' })
          }
        })
      }
    },
    watch: {
      recordType() {
        this.loadActivityAuditTypesForRecord()
      },
      resolvedRecordId() {
        this.initialize()
      },
      defaultCodeTypeId(val) {
        if (!this.showCodeType) return
        this.addCodeTypeId = val != null ? val : null
      }
    },
    created() {
      this.loadActivityAuditTypesForRecord()
    },
    mounted() {
      this.initialize()
    },
    activated() {
      this.initialize()
    }
  }
</script>

<style scoped>
  /* Match proReviewDxPivot .dx-table: flexible widths + table-layout fixed so columns shrink with the panel */
  .activity-audit-table-wrap {
    width: 100%;
    min-width: 0;
    overflow-x: auto;
  }

  .activity-audit-table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    margin-bottom: 0;
  }

  .activity-audit-table thead th {
    background-color: #f5f7fa;
    padding: 8px;
    text-align: left;
    font-weight: 600;
    vertical-align: middle;
    border-bottom: 2px solid #e2e8f0;
  }

  .activity-audit-table tbody td {
    padding: 8px;
    vertical-align: top;
  }

  /* Same as proReviewDxPivot .dx-table tbody tr — horizontal rules between all body rows */
  .activity-audit-table tbody tr {
    border-top: 1px solid #e2e8f0;
  }

  .activity-audit-table tbody tr:nth-child(even) {
    background-color: #fafafa;
  }

  .activity-audit-table tbody tr:hover {
    background-color: #f0f9ff;
  }

  .activityCascadeCol {
    width: 25%;
    min-width: 0;
    max-width: 100%;
  }

  .activity-audit-cascader {
    width: 100%;
    max-width: 100%;
  }

  .activity-audit-cascader >>> .el-input {
    font-size: 12px;
  }

  .activity-audit-cascader >>> .el-input__inner {
    max-width: 100%;
  }

  .notesCol {
    width: 28%;
    min-width: 0;
  }

  .activity-audit-notes-input {
    width: 100%;
    max-width: 100%;
    margin: 0;
    vertical-align: top;
  }

  .activity-audit-notes-input >>> .el-textarea__inner {
    word-break: break-word;
    min-width: 0;
  }

  /* Align all controls to the top of the cell (no extra margin-top vs cascader/notes) */
  .audit-cell-control {
    margin: 0 !important;
    vertical-align: top;
  }

  .codeTypeCol >>> .el-input,
  .reasonCol >>> .el-input {
    margin-top: 0 !important;
  }

  .handled-cell-inner {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-start;
    gap: 6px;
    min-width: 0;
  }

  .handled-radio-row {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    flex-wrap: nowrap;
    align-items: flex-start;
    align-self: stretch;
    margin: 0;
    padding: 0;
    min-width: 0;
    row-gap: 3px;
  }

  .handled-radio-row label {
    margin: 0;
    line-height: 1.25;
  }

  .audit-exclude-icon {
    margin-top: 5px;
    display: inline-block;
    vertical-align: middle;
  }

  .handledCol {
    width: 118px;
    min-width: 0;
    max-width: 140px;
  }

  .codeTypeCol {
    width: 120px;
    min-width: 0;
    max-width: 130px;
  }

  .excludeCol {
    width: 48px;
    min-width: 44px;
    max-width: 52px;
    text-align: center;
  }

  /* Fixed width: `width: auto` in a fixed-layout table steals all leftover space — use px like proReviewDxPivot .reasonForChangeCol */
  .reasonCol {
    width: 138px;
    min-width: 110px;
    max-width: 150px;
  }

  /* Same idea as proReviewDxPivot .deleteCol — narrow column for row delete */
  .deleteCol {
    width: 32px;
    min-width: 32px;
    max-width: 36px;
    text-align: center;
    vertical-align: top;
    padding-top: 10px;
  }

  .activity-audit-delete-x {
    margin-top: 4px;
    cursor: pointer;
  }

  .activity-audit-table .el-icon-close {
    border: 1px solid transparent;
  }

  .activity-audit-table tbody tr:nth-child(even) .el-icon-close.delete-button {
    border: 1px solid #606266;
    border-radius: 100%;
  }

  .delete-button {
    border: 1px solid transparent;
  }

  .activity-audit-reason-select {
    width: 100% !important;
    max-width: 100%;
    box-sizing: border-box;
  }

  .activity-audit-table thead th.excludeCol {
    text-align: center;
  }

  .activity-audit-table thead th.deleteCol {
    padding-left: 4px;
    padding-right: 4px;
  }

  .activity-audit-table tbody tr.add-row {
    background-color: #fff !important;
    border-top: 1px solid #e2e8f0;
  }

  .no-types-msg {
    padding: 15px;
    color: #909399;
    font-style: italic;
  }

  label {
    font-size: 12px;
    cursor: pointer;
  }

  label input[type='radio'] {
    margin-right: 5px;
  }

  .handled-missing-warn {
    flex-shrink: 0;
    align-self: flex-start;
    font-size: 16px;
    line-height: 1;
    color: #e6a23c;
    margin-top: 4px;
  }
</style>
